import apiClient from "./apiClient";

export const MENU_STORAGE_KEY = "menuItems";
export const NO_ACCESS_ROUTE = "/no-access";

const PATH_ALIASES = {
  "/dashboard": "/dashboard",
  "/dashbord": "/dashboard",
  dashboard: "/dashboard",
  "/inventory": "/records",
  inventory: "/records",
  "/records": "/records",
  records: "/records",
  "/datalist": "/datalist",
  datalist: "/datalist",
  "/custody-log": "/custody-log",
  "custody-log": "/custody-log",
  "/reports": "/reports",
  reports: "/reports",
  "/districts": "/districts",
  districts: "/districts",
  "/masters": "/masters",
  masters: "/masters",
  "/masters/districts": "/districts",
  "/sub-divisions": "/sub-divisions",
  "sub-divisions": "/sub-divisions",
  sub_divisions: "/sub-divisions",
  "/masters/sub-divisions": "/sub-divisions",
  "/stations": "/stations",
  stations: "/stations",
  "/masters/stations": "/stations",
  "/users": "/users",
  users: "/users",
  "/masters/users": "/users",
  "/law-reference": "/masters",
  "law-reference": "/masters",
  law_reference: "/masters",
  "/payments": "/payments",
  payments: "/payments",
  "/logs": "/logs",
  logs: "/logs",
  "/masters/logs": "/logs",
  "/role-access": "/role-access",
  "role-access": "/role-access",
  "/menu-access": "/role-access",
  "menu-access": "/role-access",
  role_access: "/role-access",
  menu_access: "/role-access",
  "/masters/role-access": "/role-access",
  "/view-date": "/View-Date",
  "view-date": "/View-Date",
  next_court_date: "/View-Date",
  "/next-court-date": "/View-Date",
};

const KEY_ALIASES = {
  dashboard: "/dashboard",
  inventory: "/records",
  records: "/records",
  datalist: "/datalist",
  list_of_case_property: "/datalist",
  list_muddamaal: "/datalist",
  custody_log: "/custody-log",
  history: "/custody-log",
  reports: "/reports",
  bar_chart: "/reports",
  users: "/users",
  group: "/users",
  districts: "/districts",
  map: "/districts",
  masters: "/masters",
  star: "/masters",
  sub_divisions: "/sub-divisions",
  account_tree: "/sub-divisions",
  stations: "/stations",
  apartment: "/stations",
  law_reference: "/masters",
  gavel: "/masters",
  payments: "/payments",
  logs: "/logs",
  audit_logs: "/logs",
  shield: "/logs",
  menu_access: "/role-access",
  role_access: "/role-access",
  next_court_date: "/View-Date",
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const normalizeLookupKey = (value) =>
  normalizeText(value)
    .replace(/[^\w\s/-]/g, "")
    .replace(/[-\s]+/g, "_");

const normalizePathValue = (value) => {
  const normalizedPath = normalizeText(value).replace(/\/+$/, "");
  return normalizedPath || "";
};

const extractMenuItems = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return toArray(
    payload?.menu_items ?? payload?.items ?? payload?.menu ?? payload?.data
  );
};

export const normalizeMenuPath = (path, key, label) => {
  const normalizedPath = normalizePathValue(path);

  if (PATH_ALIASES[normalizedPath]) {
    return PATH_ALIASES[normalizedPath];
  }

  const normalizedKey = normalizeLookupKey(key);
  if (KEY_ALIASES[normalizedKey]) {
    return KEY_ALIASES[normalizedKey];
  }

  const normalizedLabel = normalizeLookupKey(label);
  if (KEY_ALIASES[normalizedLabel]) {
    return KEY_ALIASES[normalizedLabel];
  }

  return String(path ?? "").trim();
};

export const normalizeAppPath = (path) => {
  const rawPath = String(path ?? "").trim();

  if (!rawPath) {
    return "";
  }

  if (rawPath.startsWith("/new-screen/")) {
    return rawPath;
  }

  if (rawPath.startsWith("/masters/districts")) {
    return "/districts";
  }

  if (rawPath.startsWith("/masters/sub-divisions")) {
    return "/sub-divisions";
  }

  if (rawPath.startsWith("/masters/stations")) {
    return "/stations";
  }

  if (rawPath.startsWith("/masters/users")) {
    return "/users";
  }

  if (rawPath.startsWith("/masters/role-access")) {
    return "/role-access";
  }

  if (rawPath.startsWith("/masters/logs")) {
    return "/logs";
  }

  const segments = rawPath.split("/").filter(Boolean);
  const firstSegment = segments.length > 0 ? `/${segments[0]}` : rawPath;

  if (PATH_ALIASES[firstSegment]) {
    return PATH_ALIASES[firstSegment];
  }

  return firstSegment || rawPath;
};

export const normalizeMenuContextPath = (path) => {
  const rawPath = String(path ?? "").trim();

  if (!rawPath) {
    return "";
  }

  if (rawPath.startsWith("/new-screen/")) {
    return "/datalist";
  }

  if (rawPath.startsWith("/report-page/")) {
    return "/reports";
  }

  if (rawPath.startsWith("/attachment/")) {
    return "/datalist";
  }

  if (rawPath.startsWith("/dataview/")) {
    return "/datalist";
  }

  if (rawPath.startsWith("/deletepopup/") || rawPath.startsWith("/confirmpopup")) {
    return "/datalist";
  }

  if (rawPath === "/masters" || rawPath.startsWith("/law-reference")) {
    return "/masters";
  }

  if (rawPath.startsWith("/masters/districts")) {
    return "/districts";
  }

  if (rawPath.startsWith("/masters/sub-divisions")) {
    return "/sub-divisions";
  }

  if (rawPath.startsWith("/masters/stations")) {
    return "/stations";
  }

  if (rawPath.startsWith("/masters/users")) {
    return "/users";
  }

  if (rawPath.startsWith("/masters/role-access")) {
    return "/role-access";
  }

  if (rawPath.startsWith("/masters/logs")) {
    return "/logs";
  }

  const segments = rawPath.split("/").filter(Boolean);
  const firstSegment = segments.length > 0 ? `/${segments[0]}` : rawPath;

  if (PATH_ALIASES[firstSegment]) {
    return PATH_ALIASES[firstSegment];
  }

  return firstSegment || rawPath;
};

const normalizeMenuItem = (item) => ({
  ...item,
  id: item?.id ?? item?.key ?? item?.path ?? item?.label,
  key: item?.key ?? "",
  label: String(item?.label ?? item?.name ?? item?.key ?? "Untitled").trim(),
  path: normalizeMenuPath(item?.path, item?.key, item?.label),
  icon: item?.icon ?? "",
  parent_id:
    item?.parent_id === undefined ||
    item?.parent_id === null ||
    item?.parent_id === "" ||
    item?.parent_id === "null"
      ? null
      : item.parent_id,
  sort_order: Number(item?.sort_order ?? 0),
  is_active:
    item?.is_active === undefined || item?.is_active === null || item?.is_active === ""
      ? true
      : item?.is_active === 1 ||
        item?.is_active === true ||
        String(item?.is_active).toLowerCase() === "true",
});

const sortMenuItems = (items) =>
  [...items].sort((firstItem, secondItem) => {
    if (firstItem.sort_order !== secondItem.sort_order) {
      return firstItem.sort_order - secondItem.sort_order;
    }

    return String(firstItem.label).localeCompare(String(secondItem.label));
  });

export const getStoredMenuItems = () => {
  try {
    const storedValue = localStorage.getItem(MENU_STORAGE_KEY);
    return toArray(storedValue ? JSON.parse(storedValue) : []);
  } catch (error) {
    console.error("Failed to parse stored menu items:", error);
    return [];
  }
};

export const clearStoredMenuItems = () => {
  localStorage.removeItem(MENU_STORAGE_KEY);
};

export const fetchUserMenu = async (authKey) => {
  const config = authKey
    ? {
        headers: {
          Authorization: authKey,
        },
      }
    : undefined;

  const response = await apiClient.get("/api/me/menu", config);
  const menuItems = sortMenuItems(
    extractMenuItems(response.data)
      .map((item) => normalizeMenuItem(item))
      .filter((item) => item.is_active)
  );

  localStorage.setItem(MENU_STORAGE_KEY, JSON.stringify(menuItems));
  return menuItems;
};

export const buildMenuTree = (menuItems) => {
  const safeMenuItems = sortMenuItems(toArray(menuItems));
  const itemMap = new Map();

  safeMenuItems.forEach((item) => {
    itemMap.set(item.id, {
      ...item,
      children: [],
    });
  });

  const rootItems = [];

  itemMap.forEach((item) => {
    if (item.parent_id && itemMap.has(item.parent_id)) {
      itemMap.get(item.parent_id).children.push(item);
      return;
    }

    rootItems.push(item);
  });

  const sortTree = (items) =>
    sortMenuItems(items).map((item) => ({
      ...item,
      children: sortTree(item.children),
    }));

  return sortTree(rootItems);
};

export const getDefaultMenuPath = (menuItems) => {
  const safeMenuItems = sortMenuItems(toArray(menuItems)).filter(
    (item) => item.is_active && item.path
  );

  const preferredPaths = ["/datalist", "/records", "/dashboard"];

  for (const path of preferredPaths) {
    const matchingItem = safeMenuItems.find((item) => item.path === path);

    if (matchingItem?.path) {
      return matchingItem.path;
    }
  }

  if (safeMenuItems[0]?.path) {
    return safeMenuItems[0].path;
  }

  return "/dashboard";
};

export const hasMenuAccess = (menuItems, path) => {
  const normalizedCurrentPath = normalizeMenuContextPath(path);

  if (!normalizedCurrentPath) {
    return false;
  }

  return sortMenuItems(toArray(menuItems))
    .filter((item) => item.is_active && item.path)
    .some((item) => {
      const normalizedMenuPath = normalizeMenuContextPath(item.path);

      if (!normalizedMenuPath) {
        return false;
      }

      return (
        normalizedCurrentPath === normalizedMenuPath ||
        normalizedCurrentPath.startsWith(`${normalizedMenuPath}/`)
      );
    });
};
